import React from 'react';
import { Link } from 'react-router-dom';

const HowItWorks = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">How RankTheBlank.js Works</h1>
      
      <div className="prose prose-indigo prose-lg text-gray-500 mx-auto">
        <h2>Creating a Poll</h2>
        <ol>
          <li>
            <strong>Sign up or log in</strong> to your RankTheBlank.js account.
          </li>
          <li>
            <strong>Click "Create Poll"</strong> from your dashboard or the navigation menu.
          </li>
          <li>
            <strong>Enter your poll details:</strong>
            <ul>
              <li>Title - What are you ranking?</li>
              <li>Description - Provide context for your voters</li>
              <li>Category - Helps organize polls</li>
              <li>Options - Add all the choices voters will rank</li>
              <li>Expiration - Set when voting will close</li>
              <li>Visibility - Public or private</li>
            </ul>
          </li>
          <li>
            <strong>Share your poll</strong> with participants using the generated link or QR code.
          </li>
        </ol>
        
        <h2>Voting in a Poll</h2>
        <ol>
          <li>
            <strong>Access the poll</strong> using the link or QR code shared by the poll creator.
          </li>
          <li>
            <strong>Drag and drop the options</strong> to rank them in your preferred order. Your first choice should be at the top.
          </li>
          <li>
            <strong>Review your rankings</strong> to ensure they reflect your preferences.
          </li>
          <li>
            <strong>Submit your vote</strong> when you're satisfied with your rankings.
          </li>
        </ol>
        
        <h2>Understanding Results</h2>
        <p>
          When viewing poll results, you'll see:
        </p>
        <ul>
          <li>
            <strong>The winner</strong> - The option with the most support after all counting rounds.
          </li>
          <li>
            <strong>Election summary</strong> - Total votes, quota to win, and number of counting rounds.
          </li>
          <li>
            <strong>Rounds breakdown</strong> - See how votes were redistributed in each round.
          </li>
        </ul>
        
        <h2>The STV Counting Process</h2>
        <p>
          RankTheBlank.js uses the Single Transferable Vote (STV) system to determine winners:
        </p>
        <ol>
          <li>
            <strong>Set a quota</strong> - The minimum votes needed to win, calculated using the Droop formula: <code>floor(total_votes / (seats + 1)) + 1</code>
          </li>
          <li>
            <strong>Count first preferences</strong> - Tally each voter's top choice.
          </li>
          <li>
            <strong>Elect candidates who reach the quota</strong> - Any option with enough first-preference votes wins immediately.
          </li>
          <li>
            <strong>Redistribute surplus votes</strong> - If a winner has more votes than needed, their surplus votes are transferred to voters' next preferences.
          </li>
          <li>
            <strong>Eliminate the weakest option</strong> - If no option reaches the quota, the option with the fewest votes is eliminated, and those votes are transferred to the next preferences.
          </li>
          <li>
            <strong>Repeat until finished</strong> - This process continues until a winner is determined.
          </li>
        </ol>
        
        <h2>Ready to try it out?</h2>
        <div className="mt-8 flex">
          <Link
            to="/signup"
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create Your First Poll
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
